import type {
  Author, Candidate, PanelInterviewReport, PanelScorecard,
  StageStep, Stage, AIInterviewReport, CVScreeningReport, TranscriptReport,
} from './types'
import { STAGES } from './types'

/* ─── People ─── */

export const ME: Author = { id: 'me', name: 'You', initials: 'Y', color: '#FD775E' }

const SARAH: Author = { id: 'sj', name: 'Sarah Jenkins', initials: 'SJ', color: '#8B5CF6', role: 'Hiring Manager', title: 'VP Finance' }
const ALEX:  Author = { id: 'af', name: 'Alex Foster',   initials: 'AF', color: '#0969DA', role: 'Peer',            title: 'Director, Corp. Finance' }
const JORDAN: Author = { id: 'jp', name: 'Jordan Park',  initials: 'JP', color: '#1A7F37', role: 'Cross-functional', title: 'Head of People' }
const MIA:   Author = { id: 'mn', name: 'Mia Nakamura',  initials: 'MN', color: '#E8624A', role: 'Skip-level',       title: 'CFO' }
const LUCAS: Author = { id: 'lr', name: 'Lucas Rivera',  initials: 'LR', color: '#9A6700', role: 'Technical',        title: 'Senior Financial Analyst' }

/* ─── Stepper helper ─── */

function stepperFor(current: Stage): StageStep[] {
  const idx = STAGES.indexOf(current)
  const dates: Partial<Record<Stage, string>> = {
    applied: 'Mar 21', cv_screen: 'Mar 22', ai_interview: 'Mar 24',
    panel_interview: 'Today', offer: '—', hired: '—',
  }
  const verdicts = {
    applied:          { key: 'received',    label: 'Received',    tone: 'neutral' as const },
    cv_screen:        { key: 'shortlist',   label: 'Shortlist',   tone: 'success' as const },
    ai_interview:     { key: 'advance',     label: 'Advance',     tone: 'success' as const },
    panel_interview:  { key: 'in_progress', label: 'In progress', tone: 'warning' as const },
  }
  const scores = {
    cv_screen: 85, ai_interview: 82, panel_interview: 88,
  }
  return STAGES.map<StageStep>((s, i) => ({
    stage: s,
    state: i < idx ? 'done' : i === idx ? 'active' : 'upcoming',
    date: dates[s],
    verdict: i <= idx ? verdicts[s as keyof typeof verdicts] : undefined,
    score: i <= idx ? scores[s as keyof typeof scores] : undefined,
  }))
}

/* ─── Hero candidate: Kayla Morgan ─── */

const KAYLA: Candidate = {
  id: 'kayla-morgan',
  name: 'Kayla Morgan',
  initials: 'KM',
  role: 'Investment Associate',
  company: 'Vanguard',
  location: 'Austin, TX',
  relocationTo: 'New York, NY (hybrid)',
  email: 'kayla.morgan@example.com',
  phone: '+1 512 555 0142',
  linkedinUrl: 'https://linkedin.com/in/kayla-morgan',
  avatarColor: '#0969DA',

  stage: 'panel_interview',
  status: 'for_review',
  daysInStage: 0,
  sources: ['Referral · A. Foster', 'Inbound'],

  overallScore: 88,
  overallConfidence: 92,
  headlineMatch: 'strong_match',

  ai: {
    headline: 'strong_match',
    hook: 'Rare double — Berkeley-trained quant with Goldman deal-execution reps.',
    summary:
      "Investment professional at Vanguard with 7 years in financial services. Quantitative foundation from UC Berkeley's M.S. in Financial Engineering, complemented by M&A execution experience at Goldman Sachs. The profile reads as a natural fit for the analytical demands of the Finance Director role; the one open question is people-leadership scope, which the panel interview has been specifically scoped to probe.",
    strengths: [
      'Deep financial engineering toolkit (DCF, comps, precedent, LBO)',
      'Top-tier academics — Berkeley Haas, undergrad honors',
      'Strong tenure signal (7+ yrs across two premier firms)',
      'Structured communicator; examples are concrete and numeric',
    ],
    growth: [
      'No direct reports on record — only informal mentorship',
      'Relocation gap: Austin-based, role is NYC hybrid',
      'Corporate-finance exposure lighter than buy-side',
    ],
  },

  icp: [
    { id: 'icp-academic',    label: 'Academic Background',          level: 'very_strong', note: 'M.S. Financial Engineering, UC Berkeley (top 3%)' },
    { id: 'icp-finance-lead',label: 'Finance Leadership Experience', level: 'strong',     note: '7 years senior IC; led two deal teams at Goldman' },
    { id: 'icp-industry',    label: 'Industry Knowledge',            level: 'moderate',   note: 'Buy-side strong; corp-finance exposure lighter' },
    { id: 'icp-mgmt',        label: 'People Management Scope',       level: 'moderate',   note: 'Mentor role; no formal direct reports' },
    { id: 'icp-location',    label: 'Location / Remote Fit',         level: 'weak',       note: 'Austin-based; open to NYC hybrid with relo' },
  ],

  radar: [
    { id: 'rd-exp',     label: 'Experience',         score: 8.5, maxScore: 10, benchmark: 6.5, note: '7 yrs across Vanguard + Goldman' },
    { id: 'rd-tech',    label: 'Technical depth',    score: 9.5, maxScore: 10, benchmark: 6.0, note: 'Berkeley M.S. + daily modelling' },
    { id: 'rd-lead',    label: 'Leadership',         score: 6.0, maxScore: 10, benchmark: 7.0, note: 'Mentor, no direct reports' },
    { id: 'rd-comms',   label: 'Communication',      score: 8.5, maxScore: 10, benchmark: 7.0 },
    { id: 'rd-culture', label: 'Culture signal',     score: 7.0, maxScore: 10, benchmark: 7.0 },
    { id: 'rd-exec',    label: 'Execution',          score: 9.0, maxScore: 10, benchmark: 7.5, note: 'Consistent Pathways ratings, ships on time' },
  ],

  stepper: stepperFor('panel_interview'),

  experience: [
    {
      id: 'e1', company: 'Vanguard', role: 'Investment Associate',
      start: 'Jun 2019', end: 'Present',
      summary: 'Equity valuation, DCF modeling, sector-level research.',
      highlights: [
        'Owned valuation model powering $1.2B fund',
        'Promoted 1 level ahead of cohort median',
        'Mentored 4 incoming analysts',
      ],
    },
    {
      id: 'e2', company: 'Goldman Sachs', role: 'Analyst, Investment Banking',
      start: 'Jul 2017', end: 'May 2019',
      summary: 'Executed 4 M&A deals in Consumer/Retail, $150M–$2.4B.',
      highlights: [
        'Deal lead on 2 of 4 transactions',
        'Built precedent database used across team',
      ],
    },
    {
      id: 'e3', company: 'UC Berkeley (Haas)', role: 'M.S. Financial Engineering',
      start: 'Aug 2015', end: 'Jun 2017',
      summary: 'Research assistantship in Quantitative Risk.',
    },
    {
      id: 'e4', company: 'Cornell University', role: 'B.S. Applied Economics, Honors',
      start: 'Aug 2011', end: 'May 2015',
    },
  ],

  skills: [
    { label: 'Financial Modelling',   proficiency: 'very_strong', yearsExperience: 7 },
    { label: 'Valuation (DCF/Comps)', proficiency: 'very_strong', yearsExperience: 7 },
    { label: 'M&A Execution',         proficiency: 'strong',      yearsExperience: 3 },
    { label: 'Python',                proficiency: 'strong',      yearsExperience: 5 },
    { label: 'SQL',                   proficiency: 'moderate',    yearsExperience: 4 },
    { label: 'Bloomberg / Capital IQ',proficiency: 'very_strong', yearsExperience: 6 },
    { label: 'Corporate Finance',     proficiency: 'moderate',    yearsExperience: 2 },
    { label: 'People Management',     proficiency: 'moderate',    yearsExperience: 1 },
  ],
  certifications: ['CFA Charterholder (2021)', 'Series 7 & 63', 'Python for Finance (Coursera)'],
  education: [
    { school: 'UC Berkeley (Haas)', degree: 'M.S. Financial Engineering', year: '2017' },
    { school: 'Cornell University', degree: 'B.S. Applied Economics, Honors', year: '2015' },
  ],
  compensation: {
    currency: 'USD',
    currentTotal: 215_000,
    askLow: 240_000,
    askHigh: 275_000,
    marketP50: 235_000,
    marketP75: 268_000,
    marketP90: 295_000,
    bonusExpectation: '20–30% target',
    equityExpectation: 'Standard new-hire grant, 4yr / 1yr cliff',
    availableFrom: '4 weeks notice',
    relocation: 'hybrid',
    notes: 'Open to NYC if relo package is included; flexible on base vs. bonus mix.',
  },
  references: [
    { id: 'rf1', name: 'Natalie Brooks',  initials: 'NB', color: '#8B5CF6', role: 'Managing Director', company: 'Vanguard', email: 'natalie.brooks@example.com', vouched: true, note: 'Direct manager 2021–present. Strongly vouched.' },
    { id: 'rf2', name: 'Ethan Goldberg',  initials: 'EG', color: '#9A6700', role: 'VP, M&A',           company: 'Goldman Sachs', vouched: true, note: 'Led deal team with Kayla 2018.' },
    { id: 'rf3', name: 'Dr. Henry Liu',   initials: 'HL', color: '#1A7F37', role: 'Professor',         company: 'UC Berkeley', vouched: true, note: 'Graduate advisor.' },
  ],
  activity: [
    { id: 'a1',  kind: 'panel',        text: 'Panel interview completed (4 of 5 scorecards submitted)', at: '1h ago',  by: 'System' },
    { id: 'a2',  kind: 'override',     text: '"Industry knowledge" overridden 5 → 7 with reasoning',     at: '2h ago',  by: 'Sarah Jenkins' },
    { id: 'a3',  kind: 'email',        text: 'Relocation package follow-up sent',                        at: '5h ago',  by: 'Alex Foster' },
    { id: 'a4',  kind: 'stage_change', text: 'Moved to Panel Interview',                                  at: 'Today',   by: 'System' },
    { id: 'a5',  kind: 'system',       text: 'AI Interview completed (22m 14s, score 82)',                at: 'Mar 24',  by: 'Lantern AI' },
    { id: 'a6',  kind: 'system',       text: 'CV screening completed (85, 5 of 5 requirements met)',      at: 'Mar 22',  by: 'Lantern AI' },
    { id: 'a7',  kind: 'note',         text: 'Referred by Alex Foster — team connection',                 at: 'Mar 21',  by: 'Alex Foster' },
    { id: 'a8',  kind: 'stage_change', text: 'Application received',                                       at: 'Mar 21',  by: 'System' },
  ],

  reports: [
    // ─── CV Screening ───
    <CVScreeningReport>{
      kind: 'cv_screen',
      headlineScore: 85,
      requirementsMet: 5,
      requirementsTotal: 5,
      processedAt: 'Processed Mar 22',
      summary: 'All five hard requirements met with supporting evidence. Weighted criteria lean strong on quantitative/technical and moderate on leadership & location.',
      requirements: [
        { id: 'r1', label: '5+ years financial services',   passed: true, note: '7 years — Vanguard (6y) + Goldman (2y)' },
        { id: 'r2', label: "Master's degree or equivalent",  passed: true, note: 'M.S. Financial Engineering, UC Berkeley' },
        { id: 'r3', label: 'Financial modelling experience', passed: true, note: 'Core of current role at Vanguard' },
        { id: 'r4', label: 'Valuation & M&A experience',     passed: true, note: 'Executed 4 deals at Goldman; 2 as lead' },
        { id: 'r5', label: 'Right to work in US',            passed: true },
      ],
      criteria: [
        {
          id: 'c1', label: 'Leadership & management', score: 6, maxScore: 10, weight: 3,
          note: 'Informal mentoring but no formal reports yet.',
          overrides: [{
            id: 'ov-1', author: SARAH, at: '2d ago', newValue: 6, oldValue: 4,
            reason: 'Panel feedback flagged stronger leadership signal than CV suggested — adjusting up.',
          }],
          comments: [{ id: 'c-1', author: SARAH, at: '2d ago', text: 'Worth probing during Panel — check the Goldman deal-lead story.' }],
        },
        { id: 'c2', label: 'Quantitative depth',        score: 10, maxScore: 10, weight: 4, note: 'MS Financial Engineering + 7 yr quant work.' },
        {
          id: 'c3', label: 'Industry-specific knowledge', score: 7, maxScore: 10, weight: 2,
          note: 'Strong buy-side; corp-finance lighter.',
          overrides: [{
            id: 'ov-2', author: SARAH, at: '2h ago', newValue: 7, oldValue: 5,
            reason: 'Speaking with her yesterday — more corporate-finance exposure from Goldman than CV highlighted.',
          }],
        },
        { id: 'c4', label: 'Location flexibility',       score: 4, maxScore: 10, weight: 1, note: 'Based in Austin, wants hybrid in NYC — relo needed.' },
      ],
    },

    // ─── AI Interview ───
    <AIInterviewReport>{
      kind: 'ai_interview',
      headlineScore: 82,
      duration: '22m 14s',
      processedAt: 'Processed Mar 24',
      summary:
        'Structured, numerate, and specific. Strongest on technical framing; less certain on people-management scenarios. The conversation length (22m) and depth of push-back (3 follow-ups accepted) signal composure.',
      qualitativeFit: { communication: 'very_strong', depth: 'strong', problemSolving: 'strong', culture: 'moderate' },
      greenFlags: [
        'Used numbers to reason under pressure (DCF TV sanity-check)',
        'Explicitly checked assumptions before answering',
        'Recovered gracefully when pushed on terminal value approach',
      ],
      redFlags: [
        'Leadership stories were thin on concrete examples',
        'Avoided a direct answer on managing underperformers',
      ],
      competencies: [
        { id: 'ic1', label: 'Structured thinking',             score: 8, maxScore: 10, weight: 2, note: 'Clear frameworks; clean recovery under push.' },
        { id: 'ic2', label: 'Technical depth',                 score: 9, maxScore: 10, weight: 3, note: 'Comfortable with DCF, comps, precedent; numerate.' },
        { id: 'ic3', label: 'Communication clarity',           score: 9, maxScore: 10, weight: 2, note: 'Concise; checks assumptions before answering.' },
        { id: 'ic4', label: 'Leadership situational judgment', score: 6, maxScore: 10, weight: 2, note: 'Good instincts; limited direct-report scenarios.' },
      ],
    },

    // ─── In-person Panel Interview (NEW) ───
    <PanelInterviewReport>{
      kind: 'panel_interview',
      scheduledAt: 'Today · 10:30 AM',
      location: 'onsite',
      durationMinutes: 240,
      overallScore: 88,
      overallRecommendation: 'yes',
      decisionThreshold: 75,
      summary:
        'Strong consensus among technical and peer panelists. The Hiring Manager and Skip-level are bullish on execution and depth; concerns cluster around people-leadership scope and relocation logistics. Panel recommends advancing to offer with a compensated relo package.',
      consensusHighlights: [
        'Strong quantitative and valuation depth — top of bar for the band',
        'Clear, structured communication under fast-paced technical Q&A',
        'Credible culture signal; team-first framing consistent across panelists',
      ],
      consensusConcerns: [
        'Limited direct-report experience — gap vs. ideal profile',
        'Austin → NYC relocation adds a 4–6 week window',
        'Compensation ask sits between P75 and P90 of band',
      ],
      nextSteps: [
        'Hiring Manager prepares offer at P75 with relo package',
        'Reference calls (3 of 3 confirmed) scheduled Thu–Fri',
        'Kayla to confirm earliest NYC start by EOW',
      ],
      scorecards: buildPanelScorecards(),
    },

    // ─── Transcript (panel) ───
    <TranscriptReport>{
      kind: 'transcript',
      source: 'panel_interview',
      duration: '4h 00m (across 5 panelists)',
      keyMoments: [
        { at: '00:18:42', label: 'DCF sanity-check',     note: 'Kayla sanity-checks perpetuity vs exit-multiple within 4%.' },
        { at: '01:52:10', label: 'Leadership example',   note: 'Navigated VP vs analyst disagreement on valuation floor.' },
        { at: '03:10:04', label: 'Relocation discussion', note: 'Open to NYC hybrid; flexible on comp split.' },
      ],
      turns: [
        { id: 't1', speaker: 'interviewer', speakerName: 'Sarah Jenkins', at: '00:00:40', text: "Walk me through a DCF you built for an acquisition target." },
        { id: 't2', speaker: 'candidate',   speakerName: 'Kayla Morgan',  at: '00:01:12', text: "Sure — at Vanguard I built a DCF for a regional bank target. I normalized 3 years of history, adjusted for the 2022 one-time restructuring charge, and projected cash flows out 5 years with a Gordon Growth tail.", highlight: true },
        { id: 't3', speaker: 'interviewer', speakerName: 'Lucas Rivera',  at: '00:04:30', text: "How did you pick the 2% perpetuity rate?" },
        { id: 't4', speaker: 'candidate',   speakerName: 'Kayla Morgan',  at: '00:04:42', text: "Long-run inflation target plus a small real-growth premium. I stress-tested ±100bps and the enterprise value moved ~9%.", highlight: true },
        { id: 't5', speaker: 'interviewer', speakerName: 'Jordan Park',   at: '01:48:00', text: "Tell me about a time you led a team through disagreement." },
        { id: 't6', speaker: 'candidate',   speakerName: 'Kayla Morgan',  at: '01:48:22', text: "At Goldman the VP and analyst disagreed on our valuation floor. I ran a blind sensitivity in private, shared it as a neutral third option, and we landed at a split-the-difference that both signed off on." },
        { id: 't7', speaker: 'interviewer', speakerName: 'Mia Nakamura',  at: '03:05:30', text: "How do you feel about relocating to New York?" },
        { id: 't8', speaker: 'candidate',   speakerName: 'Kayla Morgan',  at: '03:05:48', text: "Open to it — my partner works remote. I'd want a hybrid schedule and a relocation package that covers the first 90 days." },
      ],
    },
  ],

  notes: [
    { id: 'n1', author: SARAH, at: 'Mar 22', text: 'Strong CV. Worth advancing despite location gap — team is open to hybrid with relo.' },
    { id: 'n2', author: ALEX,  at: 'Mar 25', text: 'AI interview went well. Leadership was the one wobble — scoped the panel to probe it live.' },
    { id: 'n3', author: JORDAN,at: 'Today',  text: 'Culture panel was positive — team-first framing came through on two different stories.' },
  ],
  files: [
    { id: 'f1', name: 'Kayla_Morgan_CV.pdf',        kind: 'pdf', size: '312 KB', uploadedAt: 'Mar 21' },
    { id: 'f2', name: 'Cover_Letter_FinanceDir.pdf', kind: 'pdf', size: '98 KB',  uploadedAt: 'Mar 21' },
    { id: 'f3', name: 'Deal_Sheet_2019-2024.pdf',    kind: 'pdf', size: '1.2 MB', uploadedAt: 'Mar 21' },
    { id: 'f4', name: 'Recommendation_N_Brooks.pdf', kind: 'pdf', size: '86 KB',  uploadedAt: 'Mar 22' },
    { id: 'f5', name: 'Berkeley_Transcript.pdf',     kind: 'pdf', size: '510 KB', uploadedAt: 'Mar 21' },
  ],
}

/* ─── Panel scorecards builder ─── */

function buildPanelScorecards(): PanelScorecard[] {
  return [
    {
      id: 'ps-sarah',
      interviewer: SARAH, panelRole: 'Hiring Manager',
      recommendation: 'yes',
      confidence: 'high',
      narrative:
        "Kayla clears the analytical bar comfortably. I pushed hard on the leadership questions and her answers were competent but not yet senior — coachable. Given the strength of her technical profile and the referral signal, I'm a clear Yes with a structured 30/60/90 management runway.",
      highlights: ['Structured, numerate reasoning', 'Owned the Goldman valuation-floor example'],
      concerns: ['Leadership scope lighter than ideal'],
      submittedAt: '1h ago',
      competencies: [
        { id: 'sc-1', label: 'Technical / analytical',         score: 9,  maxScore: 10, weight: 3, note: 'Strong DCF sanity-check instinct.' },
        { id: 'sc-2', label: 'Leadership readiness',            score: 6,  maxScore: 10, weight: 3, note: 'Coachable but not demonstrated at scale.' },
        { id: 'sc-3', label: 'Business judgment',               score: 8,  maxScore: 10, weight: 2, note: 'Asked the right framing questions.' },
        { id: 'sc-4', label: 'Ownership & drive',               score: 9,  maxScore: 10, weight: 2, note: 'Promoted 1 level ahead of cohort.' },
      ],
    },
    {
      id: 'ps-alex',
      interviewer: ALEX, panelRole: 'Peer',
      recommendation: 'strong_yes',
      confidence: 'high',
      narrative:
        "Best technical candidate I've seen this cycle. Comfortable with the full valuation toolkit and didn't need to be led. I'd work with her tomorrow.",
      highlights: ['Fast technical back-and-forth', 'Proposed a cleaner way to model the restructuring adjustment'],
      concerns: [],
      submittedAt: '1h ago',
      competencies: [
        { id: 'sc-5', label: 'Technical depth',                 score: 10, maxScore: 10, weight: 3 },
        { id: 'sc-6', label: 'Collaboration',                   score: 9,  maxScore: 10, weight: 2 },
        { id: 'sc-7', label: 'Communication clarity',           score: 9,  maxScore: 10, weight: 2 },
      ],
    },
    {
      id: 'ps-jordan',
      interviewer: JORDAN, panelRole: 'Cross-functional',
      recommendation: 'yes',
      confidence: 'medium',
      narrative:
        'Culture signal is positive. Team-first framing on two different stories. Watch item: values on underperformer conversations were a touch deflective — would clarify with Hiring Manager.',
      highlights: ['Team-first framing repeated naturally', 'Asked thoughtful questions about onboarding'],
      concerns: ['Deflective on underperformer scenario'],
      submittedAt: '45m ago',
      competencies: [
        { id: 'sc-8',  label: 'Culture alignment',              score: 8, maxScore: 10, weight: 3 },
        { id: 'sc-9',  label: 'Emotional intelligence',         score: 7, maxScore: 10, weight: 2 },
        { id: 'sc-10', label: 'Feedback orientation',           score: 6, maxScore: 10, weight: 2, note: 'A little deflective on the underperformer scenario.' },
      ],
    },
    {
      id: 'ps-mia',
      interviewer: MIA, panelRole: 'Skip-level',
      recommendation: 'lean_yes',
      confidence: 'medium',
      narrative:
        "Strong individual contributor candidate. My concern is whether the role is the right step — she's applying for a Director seat but hasn't managed before. Would advance with a clear 6-month bar around team expansion.",
      highlights: ['Executive presence stronger than CV suggested', 'Clear on the problem space'],
      concerns: ['Role-scope match: Director level, IC history'],
      submittedAt: '30m ago',
      competencies: [
        { id: 'sc-11', label: 'Strategic thinking',             score: 8, maxScore: 10, weight: 2 },
        { id: 'sc-12', label: 'Executive presence',             score: 8, maxScore: 10, weight: 2 },
        { id: 'sc-13', label: 'Role-scope match',               score: 6, maxScore: 10, weight: 3, note: 'Director level, IC history.' },
      ],
    },
    {
      id: 'ps-lucas',
      interviewer: LUCAS, panelRole: 'Technical',
      recommendation: 'strong_yes',
      confidence: 'high',
      narrative:
        'Cleanest technical round of the loop. Could answer three-layer follow-ups without losing framing. Hire.',
      highlights: ['Clean terminal-value stress test', 'Explicitly sanity-checked results both ways'],
      concerns: [],
      submittedAt: '20m ago',
      competencies: [
        { id: 'sc-14', label: 'Valuation & modelling',          score: 10, maxScore: 10, weight: 3 },
        { id: 'sc-15', label: 'Problem-solving under pressure', score: 9,  maxScore: 10, weight: 2 },
      ],
    },
  ]
}

/* ─── Lite candidates for the kanban ─── */

function mkLite(p: {
  id: string; name: string; initials: string; role: string; company: string
  color: string; stage: Stage; score: number
  match: import('./types').HeadlineMatch; days: number
}): Candidate {
  return {
    id: p.id, name: p.name, initials: p.initials, role: p.role, company: p.company,
    avatarColor: p.color, stage: p.stage, status: 'in_progress', daysInStage: p.days,
    overallScore: p.score, overallConfidence: 70 + Math.floor(p.score / 5), headlineMatch: p.match,
    sources: ['Direct'],
    ai: {
      headline: p.match,
      summary: `${p.name.split(' ')[0]} brings a strong background at ${p.company}. Review the per-stage reports for details.`,
      strengths: ['Relevant experience', 'Clear communication'],
      growth: ['Needs deeper culture-fit review'],
    },
    icp: [
      { id: 'icp-1', label: 'Relevant experience', level: p.score >= 85 ? 'very_strong' : p.score >= 70 ? 'strong' : 'moderate' },
      { id: 'icp-2', label: 'Technical depth',     level: p.score >= 85 ? 'very_strong' : p.score >= 70 ? 'strong' : 'moderate' },
      { id: 'icp-3', label: 'Communication',       level: p.score >= 80 ? 'strong' : 'moderate' },
      { id: 'icp-4', label: 'Culture signal',      level: 'moderate' },
    ],
    radar: [
      { id: 'r1', label: 'Experience',      score: p.score / 12, maxScore: 10, benchmark: 6 },
      { id: 'r2', label: 'Technical depth', score: p.score / 11, maxScore: 10, benchmark: 6 },
      { id: 'r3', label: 'Leadership',      score: p.score / 14, maxScore: 10, benchmark: 7 },
      { id: 'r4', label: 'Communication',   score: p.score / 12, maxScore: 10, benchmark: 7 },
      { id: 'r5', label: 'Culture',         score: p.score / 14, maxScore: 10, benchmark: 7 },
      { id: 'r6', label: 'Execution',       score: p.score / 12, maxScore: 10, benchmark: 7 },
    ],
    stepper: stepperFor(p.stage),
    experience: [
      { id: 'e1', company: p.company, role: p.role, start: 'Jun 2020', end: 'Present' },
    ],
    skills: [
      { label: 'React',      proficiency: 'strong' },
      { label: 'TypeScript', proficiency: 'strong' },
    ],
    activity: [
      { id: 'a1', kind: 'stage_change', text: `Moved to ${p.stage.replace('_', ' ')}`, at: `${p.days}d ago` },
    ],
    reports: [
      <CVScreeningReport>{
        kind: 'cv_screen',
        headlineScore: p.score,
        requirementsMet: p.score >= 70 ? 4 : 3,
        requirementsTotal: 5,
        processedAt: 'Processed recently',
        summary: 'Auto-generated preview.',
        requirements: [
          { id: 'r1', label: '5+ years experience',  passed: p.score >= 60 },
          { id: 'r2', label: 'Framework proficiency',passed: p.score >= 50 },
          { id: 'r3', label: 'Design-system work',   passed: p.score >= 70 },
          { id: 'r4', label: 'Startup exposure',     passed: p.score >= 85 },
          { id: 'r5', label: 'US / EU timezone',     passed: true },
        ],
        criteria: [
          { id: 'c1', label: 'Relevant experience', score: Math.round(p.score / 10), maxScore: 10 },
          { id: 'c2', label: 'Technical depth',     score: Math.round(p.score / 10), maxScore: 10 },
          { id: 'c3', label: 'Communication',       score: Math.round((p.score - 5) / 10), maxScore: 10 },
          { id: 'c4', label: 'Scope & impact',      score: Math.round((p.score - 10) / 10), maxScore: 10 },
        ],
      },
    ],
    notes: [], files: [],
  }
}

/* ─── Public board ─── */

export const CANDIDATES: Candidate[] = [
  KAYLA,
  mkLite({ id: 'sarah-chen',   name: 'Sarah Chen',    initials: 'SC', role: 'Senior Frontend Engineer', company: 'Stripe',   color: '#E8624A', stage: 'cv_screen',        score: 92, match: 'strong_match', days: 1 }),
  mkLite({ id: 'aisha-patel',  name: 'Aisha Patel',   initials: 'AP', role: 'Senior Engineer',          company: 'Linear',   color: '#0969DA', stage: 'ai_interview',     score: 88, match: 'strong_match', days: 3 }),
  mkLite({ id: 'elena-volkov', name: 'Elena Volkov',  initials: 'EV', role: 'Staff Engineer',           company: 'GitHub',   color: '#CF222E', stage: 'panel_interview',  score: 95, match: 'strong_match', days: 2 }),
  mkLite({ id: 'alex-rivera',  name: 'Alex Rivera',   initials: 'AR', role: 'Senior Engineer',          company: 'Meta',     color: '#8B5CF6', stage: 'offer',            score: 91, match: 'strong_match', days: 1 }),
  mkLite({ id: 'james-wilson', name: 'James Wilson',  initials: 'JW', role: 'Frontend Developer',       company: 'Shopify',  color: '#8B5CF6', stage: 'applied',          score: 72, match: 'match',         days: 2 }),
]

export function getCandidate(id: string): Candidate | undefined {
  return CANDIDATES.find((c) => c.id === id)
}
export function getCandidates(): Candidate[] { return CANDIDATES }
